import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';

const ACTION_COLORS = {
  CREATE: { bg: '#d1fae5', color: '#059669' },
  UPDATE: { bg: '#dbeafe', color: '#1d4ed8' },
  DELETE: { bg: '#fee2e2', color: '#dc2626' },
  LOGIN:  { bg: '#f1f5f9', color: '#64748b' },
};

const ACTIONS = ['', 'CREATE', 'UPDATE', 'DELETE', 'LOGIN'];
const ENTITIES = ['', 'customer', 'client', 'contact', 'order', 'inquiry', 'project', 'catalog', 'product', 'visit', 'user', 'public_inquiry'];

function ActionBadge({ action }) {
  const s = ACTION_COLORS[action] || { bg: '#f1f5f9', color: '#64748b' };
  return (
    <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: s.bg, color: s.color, letterSpacing: '0.3px' }}>
      {action}
    </span>
  );
}

function JsonViewer({ data }) {
  if (!data) return <span style={{ color: '#94a3b8' }}>—</span>;
  try {
    const parsed = typeof data === 'string' ? JSON.parse(data) : data;
    return (
      <pre style={{ margin: 0, fontSize: '0.75rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px', overflowX: 'auto', maxWidth: 400, color: '#374151', lineHeight: 1.5 }}>
        {JSON.stringify(parsed, null, 2)}
      </pre>
    );
  } catch {
    return <span style={{ fontSize: '0.8rem', color: '#475569' }}>{String(data)}</span>;
  }
}

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [filters, setFilters] = useState({ action: '', entity: '', search: '', from_date: '', to_date: '' });
  const LIMIT = 20;

  const load = useCallback(async (p, f) => {
    setLoading(true);
    try {
      const params = { page: p, limit: LIMIT };
      if (f.action) params.action = f.action;
      if (f.entity) params.entity = f.entity;
      if (f.search) params.search = f.search;
      if (f.from_date) params.from_date = f.from_date;
      if (f.to_date) params.to_date = f.to_date;
      const { data } = await api.get('/audit-logs', { params });
      setLogs(data.data);
      setTotal(data.total);
    } catch (_) {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(page, filters); }, [page, filters, load]);

  const setFilter = (key, val) => { setPage(1); setFilters(f => ({ ...f, [key]: val })); };

  const totalPages = Math.ceil(total / LIMIT);

  const fmtDate = (ts) => {
    if (!ts) return '—';
    return new Date(ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.action)    params.set('action', filters.action);
      if (filters.entity)    params.set('entity', filters.entity);
      if (filters.from_date) params.set('from_date', filters.from_date);
      if (filters.to_date)   params.set('to_date', filters.to_date);
      const res = await api.get(`/export/audit-logs?${params}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${Date.now()}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  return (
    <div style={{ padding: '0' }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ fontSize: '1.35rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>Audit Logs</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Track all create, update, delete, and login events across the system.</p>
        </div>
        <button className="btn btn-secondary" onClick={handleExport} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
          Export XLSX
        </button>
      </div>

      {/* Filters */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid var(--border)', padding: '16px 18px', marginBottom: 20, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={filters.search}
          onChange={e => setFilter('search', e.target.value)}
          placeholder="Search user, entity…"
          style={{ flex: 1, minWidth: 160, padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: '0.875rem', outline: 'none' }}
        />
        <select value={filters.action} onChange={e => setFilter('action', e.target.value)}
          style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: '0.875rem', background: '#fff', outline: 'none', cursor: 'pointer' }}>
          {ACTIONS.map(a => <option key={a} value={a}>{a || 'All Actions'}</option>)}
        </select>
        <select value={filters.entity} onChange={e => setFilter('entity', e.target.value)}
          style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: '0.875rem', background: '#fff', outline: 'none', cursor: 'pointer' }}>
          {ENTITIES.map(e => <option key={e} value={e}>{e || 'All Entities'}</option>)}
        </select>
        <input type="date" value={filters.from_date} onChange={e => setFilter('from_date', e.target.value)}
          style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: '0.875rem', outline: 'none' }}
          title="From date" />
        <input type="date" value={filters.to_date} onChange={e => setFilter('to_date', e.target.value)}
          style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: '0.875rem', outline: 'none' }}
          title="To date" />
        {(filters.action || filters.entity || filters.search || filters.from_date || filters.to_date) && (
          <button onClick={() => { setPage(1); setFilters({ action: '', entity: '', search: '', from_date: '', to_date: '' }); }}
            style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border)', background: '#fff', cursor: 'pointer', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
            Clear
          </button>
        )}
        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', whiteSpace: 'nowrap', marginLeft: 'auto' }}>
          {total} record{total !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <div className="spinner spinner-lg" />
          </div>
        ) : logs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-muted)' }}>No audit log entries found.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
                  {['Timestamp', 'User', 'Role', 'Action', 'Entity', 'Record', 'Details'].map(h => (
                    <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <React.Fragment key={log.id}>
                    <tr style={{ borderBottom: '1px solid var(--border)', transition: 'background 0.1s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <td style={{ padding: '10px 14px', color: 'var(--text-muted)', whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                        {fmtDate(log.created_at)}
                      </td>
                      <td style={{ padding: '10px 14px', fontWeight: 500, color: 'var(--text-primary)' }}>
                        {log.user_name || <span style={{ color: 'var(--text-muted)' }}>Public</span>}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        {log.user_role ? (
                          <span style={{ fontSize: '0.72rem', fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: 'var(--primary-lighter)', color: 'var(--primary)', textTransform: 'capitalize' }}>
                            {log.user_role}
                          </span>
                        ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <ActionBadge action={log.action} />
                      </td>
                      <td style={{ padding: '10px 14px', color: 'var(--text-secondary)', textTransform: 'capitalize' }}>
                        {log.entity?.replace('_', ' ')}
                      </td>
                      <td style={{ padding: '10px 14px', maxWidth: 180 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>
                          {log.entity_name || (log.entity_id ? `#${log.entity_id}` : '—')}
                        </div>
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        {(log.old_values || log.new_values) && (
                          <button
                            onClick={() => setExpanded(expanded === log.id ? null : log.id)}
                            style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', padding: '4px 10px', fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                            {expanded === log.id ? 'Hide' : 'View'}
                          </button>
                        )}
                      </td>
                    </tr>
                    {expanded === log.id && (
                      <tr style={{ background: 'var(--surface-2)' }}>
                        <td colSpan={7} style={{ padding: '14px 18px' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            <div>
                              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.5px' }}>Before</div>
                              <JsonViewer data={log.old_values} />
                            </div>
                            <div>
                              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.5px' }}>After</div>
                              <JsonViewer data={log.new_values} />
                            </div>
                          </div>
                          {log.ip_address && (
                            <div style={{ marginTop: 10, fontSize: '0.75rem', color: 'var(--text-muted)' }}>IP: {log.ip_address}</div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 24 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            style={{ padding: '7px 16px', borderRadius: 8, border: '1px solid var(--border)', background: '#fff', cursor: page === 1 ? 'not-allowed' : 'pointer', color: page === 1 ? 'var(--border-dark)' : 'var(--primary)', fontWeight: 600, fontSize: '0.875rem' }}>
            ← Prev
          </button>
          <span style={{ display: 'flex', alignItems: 'center', fontSize: '0.875rem', color: 'var(--text-secondary)', padding: '0 8px' }}>
            Page {page} of {totalPages}
          </span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            style={{ padding: '7px 16px', borderRadius: 8, border: '1px solid var(--border)', background: '#fff', cursor: page === totalPages ? 'not-allowed' : 'pointer', color: page === totalPages ? 'var(--border-dark)' : 'var(--primary)', fontWeight: 600, fontSize: '0.875rem' }}>
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
